import type {
  DateRangeTimePickerProps as DateRangeTimePickerNewProps,
  DateTimePickerProps as DateTimePickerNewProps,
  DisplayMode as DatePickerDisplayMode,
} from '../types';
import type {
  DatePickerProps as DatePickerLegacyProps,
  DateRangePickerProps as DateRangePickerLegacyProps,
  DateRangeTimePickerProps as DateRangeTimePickerLegacyProps,
  DateTimePickerProps as DateTimePickerLegacyProps,
  DisplayMode as DatePickerLegacyDisplayMode,
} from '../types.old';
import type { DateMultiPickerProps as DateMultiPickerNewProps } from './DateMultiPickerCompat';
import type { DatePickerProps as DatePickerNewProps } from './DatePickerCompat';
import type { DatePickerV2Props as DatePickerV2NewProps } from './DatePickerV2';
import type { DateRangePickerProps as DateRangePickerNewProps } from './DateRangePickerCompat';
import type { DateUnitPickerProps as DateUnitPickerNewProps } from './DateUnitPickerCompat';

type DatePickerBaseNewProp = 'status' | 'open' | 'hasRemoveButton' | 'displayMode';

export interface OldDateMultiPickerProps extends Omit<DateMultiPickerNewProps, DatePickerBaseNewProp> {
  /** @deprecated status를 대신 사용해주세요. */
  error?: boolean;
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated hasRemoveButton을 대신 사용해주세요. */
  showRemoveButton?: boolean;
  /** @deprecated displayMode='bottom-sheet'를 대신 사용해주세요. */
  displayMode?: DatePickerLegacyDisplayMode;
}

export interface OldDatePickerProps
  extends Omit<
    DatePickerNewProps,
    DatePickerBaseNewProp | 'hasTodayButton' | 'hasTimePicker' | 'hasFooter' | 'timePickerProps'
  > {
  /** @deprecated status를 대신 사용해주세요. */
  error?: boolean;
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated hasRemoveButton을 대신 사용해주세요. */
  showRemoveButton?: boolean;
  /** @deprecated hasTodayButton을 대신 사용해주세요. */
  showTodayButton?: boolean;
  /** @deprecated hasTimePicker를 대신 사용해주세요. */
  showTimePicker?: boolean;
  /** @deprecated hasFooter를 대신 사용해주세요. */
  showFooter?: boolean;
  /** @deprecated displayMode='bottom-sheet'를 대신 사용해주세요. */
  displayMode?: DatePickerLegacyDisplayMode;
  timePickerProps?: DatePickerLegacyProps['timePickerProps'];
}

export interface OldDatePickerV2Props
  extends Omit<
    DatePickerV2NewProps,
    | 'status'
    | 'displayMode'
    | 'hasRemoveButton'
    | 'hasDatePreset'
    | 'hasRadioGroupDatePreset'
    | 'hasTodayButton'
    | 'hasControls'
    | 'hasRange'
  > {
  /** @deprecated status를 대신 사용해주세요. */
  error?: boolean;
  /** @deprecated hasRemoveButton을 대신 사용해주세요. */
  showRemoveButton?: boolean;
  /** @deprecated hasDatePreset을 대신 사용해주세요. */
  showDatePreset?: boolean;
  /** @deprecated hasRadioGroupDatePreset을 대신 사용해주세요. */
  showRadioGroupDatePreset?: boolean;
  /** @deprecated hasTodayButton을 대신 사용해주세요. */
  showTodayButton?: boolean;
  /** @deprecated hasControls를 대신 사용해주세요. */
  showControls?: boolean;
  /** @deprecated hasRange를 대신 사용해주세요. */
  showRange?: boolean;
  /** @deprecated displayMode='bottom-sheet'를 대신 사용해주세요. */
  displayMode?: DatePickerLegacyDisplayMode;
}

export interface OldDateRangePickerProps
  extends Omit<
    DateRangePickerNewProps,
    | DatePickerBaseNewProp
    | 'hasDatePreset'
    | 'hasRadioGroupDatePreset'
    | 'hasTodayButton'
    | 'hasTimePicker'
    | 'timePickerProps'
  > {
  /** @deprecated status를 대신 사용해주세요. */
  error?: boolean;
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated hasRemoveButton을 대신 사용해주세요. */
  showRemoveButton?: boolean;
  /** @deprecated hasDatePreset을 대신 사용해주세요. */
  showDatePreset?: boolean;
  /** @deprecated hasRadioGroupDatePreset을 대신 사용해주세요. */
  showRadioGroupDatePreset?: boolean;
  /** @deprecated hasTodayButton을 대신 사용해주세요. */
  showTodayButton?: boolean;
  /** @deprecated hasTimePicker를 대신 사용해주세요. */
  showTimePicker?: boolean;
  /** @deprecated displayMode='bottom-sheet'를 대신 사용해주세요. */
  displayMode?: DatePickerLegacyDisplayMode;
  timePickerProps?: DateRangePickerLegacyProps['timePickerProps'];
}

export interface OldDateUnitPickerProps extends Omit<DateUnitPickerNewProps, DatePickerBaseNewProp> {
  /** @deprecated status를 대신 사용해주세요. */
  error?: boolean;
  /** @deprecated open을 대신 사용해주세요. */
  opened?: boolean;
  /** @deprecated hasRemoveButton을 대신 사용해주세요. */
  showRemoveButton?: boolean;
  /** @deprecated displayMode='bottom-sheet'를 대신 사용해주세요. */
  displayMode?: DatePickerLegacyDisplayMode;
}

function normalizeDisplayMode(displayMode?: DatePickerLegacyDisplayMode): DatePickerDisplayMode | undefined {
  return displayMode === 'bottomSheet' ? 'bottom-sheet' : displayMode;
}

function normalizeTimePickerDisplayMode(
  displayMode?: DateTimePickerLegacyProps['displayMode'],
): DateTimePickerNewProps['displayMode'] {
  return displayMode === 'bottomSheet' ? 'bottom-sheet' : displayMode;
}

export function normalizeDateTimePickerProps(
  timePickerProps?: DateTimePickerLegacyProps,
): DateTimePickerNewProps | undefined {
  if (timePickerProps == null) {
    return undefined;
  }
  const { error, displayMode, showRemoveButton, ...restProps } = timePickerProps;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    displayMode: normalizeTimePickerDisplayMode(displayMode),
    hasRemoveButton: showRemoveButton,
  };
}

export function normalizeDateRangeTimePickerProps(
  timePickerProps?: DateRangeTimePickerLegacyProps,
): DateRangeTimePickerNewProps | undefined {
  if (timePickerProps == null) {
    return undefined;
  }
  const { error, displayMode, showRemoveButton, ...restProps } = timePickerProps;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    displayMode: normalizeTimePickerDisplayMode(displayMode),
    hasRemoveButton: showRemoveButton,
  };
}

export function convertOldDateMultiPickerProps(props: OldDateMultiPickerProps): DateMultiPickerNewProps {
  const { error, opened, showRemoveButton, displayMode, ...restProps } = props;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    open: opened,
    hasRemoveButton: showRemoveButton,
    displayMode: normalizeDisplayMode(displayMode),
  };
}

export function convertOldDatePickerProps(props: OldDatePickerProps): DatePickerNewProps {
  const {
    error,
    opened,
    showRemoveButton,
    showTodayButton,
    showTimePicker,
    showFooter,
    displayMode,
    timePickerProps,
    ...restProps
  } = props;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    open: opened,
    hasRemoveButton: showRemoveButton,
    hasTodayButton: showTodayButton,
    hasTimePicker: showTimePicker,
    hasFooter: showFooter,
    displayMode: normalizeDisplayMode(displayMode),
    timePickerProps: normalizeDateTimePickerProps(timePickerProps),
  };
}

export function convertOldDatePickerV2Props(props: OldDatePickerV2Props): DatePickerV2NewProps {
  const {
    error,
    showRemoveButton,
    showDatePreset,
    showRadioGroupDatePreset,
    showTodayButton,
    showControls,
    showRange,
    displayMode,
    ...restProps
  } = props;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    hasRemoveButton: showRemoveButton,
    hasDatePreset: showDatePreset,
    hasRadioGroupDatePreset: showRadioGroupDatePreset,
    hasTodayButton: showTodayButton,
    hasControls: showControls,
    hasRange: showRange,
    displayMode: normalizeDisplayMode(displayMode),
  };
}

export function convertOldDateRangePickerProps(props: OldDateRangePickerProps): DateRangePickerNewProps {
  const {
    error,
    opened,
    showRemoveButton,
    showDatePreset,
    showRadioGroupDatePreset,
    showTodayButton,
    showTimePicker,
    displayMode,
    timePickerProps,
    ...restProps
  } = props;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    open: opened,
    hasRemoveButton: showRemoveButton,
    hasDatePreset: showDatePreset,
    hasRadioGroupDatePreset: showRadioGroupDatePreset,
    hasTodayButton: showTodayButton,
    hasTimePicker: showTimePicker,
    displayMode: normalizeDisplayMode(displayMode),
    timePickerProps: normalizeDateRangeTimePickerProps(timePickerProps),
  };
}

export function convertOldDateUnitPickerProps(props: OldDateUnitPickerProps): DateUnitPickerNewProps {
  const { error, opened, showRemoveButton, displayMode, ...restProps } = props;
  return {
    ...restProps,
    status: error ? 'error' : undefined,
    open: opened,
    hasRemoveButton: showRemoveButton,
    displayMode: normalizeDisplayMode(displayMode),
  };
}
